<template>
  <date-range-picker
    class="w-100 w-md-auto"
    ref="picker"
    :opens="position"
    :locale-data="{ firstDay: 1, format: 'dd/mm/yyyy' }"
    :close-on-esc="false"
    :showDropdowns="true"
    :ranges="ranges"
    min-date="01-01-2018"
    :max-date="theme === 'Classic' ? new Date().toLocaleDateString() : null"
    control-container-class="form-control max-h-45px"
    v-model="dateRange"
    @update="updateDateValue"
  >
    <!--            @select="selectDate"-->
    <template v-slot:input="picker">
      <div class="h-100 d-flex align-center">
        <span class="svg-icon svg-icon-2 me-2">
          <v-icon size="22">mdi-calendar</v-icon>
        </span>

        <input
          type="text"
          class="custom-input"
          :placeholder="$t('dashboard_select_a_date')"
          :value="picker.rangeText === ' - ' ? '' : picker.rangeText"
        />
      </div>
    </template>

    <template v-slot:footer="picker">
      <div
        :class="[
          { 'drp-buttons py-4': theme === 'Classic' },
          { 'py-3': theme === 'Basic' },
          'px-6 poppins d-flex align-items-center justify-content-between flex-wrap',
          { 'flex-row-reverse': $vuetify.rtl },
        ]"
      >
        <div class="d-flex align-items-center date-picker-theme-switch-wrap">
          <!--
            Vuetify places the switch thumb using LTR geometry; wrapping with
            `direction: ltr` keeps it aligned on the track when the page is RTL.
          -->
          <v-switch
            v-model="theme"
            dense
            flat
            inset
            @change="setThemeType"
            true-value="Classic"
            false-value="Basic"
            class="mt-0 date-picker-theme-switch"
          >
            <template v-slot:label
              ><span class="font-size-lg font-weight-bolder">{{
                themeLabel
              }}</span></template
            >
          </v-switch>
        </div>
        <div class="d-flex align-items-center flex-shrink-0 mt-2 mt-sm-0">
          <button
            type="button"
            class="btn btn-light ls1 py-2 px-7 me-3"
            @click="picker.clickCancel"
          >
            {{ $t("dashboard_cancel") }}</button
          ><button
            @click="picker.clickApply"
            type="button"
            class="btn btn--print-upload ls1 py-2 px-4"
          >
            {{ $t("dashboard_apply") }}
          </button>
        </div>
      </div>
    </template>
  </date-range-picker>
</template>

<script>
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";

export default {
  name: "DateTimePicker",
  components: { DateRangePicker },
  props: {
    position: { type: String, default: "center" },
  },
  data() {
    return {
      dateRange: {
        startDate: this.today(),
        endDate: this.today(),
      },
      theme: "",
    };
  },
  methods: {
    dataOfFilters() {
      let startDate, endDate;
      const pattern = /^\d{1,2}\/\d{1,2}\/\d{2,4}$/;

      if (pattern.test(this.dateRange.startDate)) {
        startDate = this.slashSeperatedToISO(this.dateRange.startDate);
        endDate = this.slashSeperatedToISO(this.dateRange.endDate);
      } else {
        startDate = this.extendedToISO(this.dateRange.startDate);
        endDate = this.extendedToISO(this.dateRange.endDate);
      }
      const filterData = {
        date_range_type: "custom",
        date_range: [startDate, endDate],
        ...this.filterData,
      };
      return filterData;
    },
    slashSeperatedToISO(date) {
      const [month, day, year] = date.split("/");
      const js_date = new Date(year, month - 1, day);
      return this.dateFormatter(js_date);
    },
    extendedToISO(val) {
      const date = new Date(val);
      return this.dateFormatter(date);
    },
    today() {
      const d = new Date();
      return this.dateFormatter(d);
    },
    updateDateValue() {
      let startDate, endDate;
      const pattern = /^\d{1,2}\/\d{1,2}\/\d{2,4}$/;

      if (pattern.test(this.dateRange.startDate)) {
        startDate = this.slashSeperatedToISO(this.dateRange.startDate);
        endDate = this.slashSeperatedToISO(this.dateRange.endDate);
      } else {
        startDate = this.extendedToISO(this.dateRange.startDate);
        endDate = this.extendedToISO(this.dateRange.endDate);
      }

      this.$emit("on-submit", [startDate, endDate]);
    },
    dateFormatter(date, charForJoin = "-") {
      return [
        date.getFullYear(),
        date.getMonth() + 1 > 9
          ? date.getMonth() + 1
          : `0${date.getMonth() + 1}`,
        date.getDate() > 9 ? date.getDate() : `0${date.getDate()}`,
      ].join(charForJoin);
    },
    getThemeType() {
      let theme = localStorage.getItem("date-picker-style");
      if (!theme) {
        this.theme = "Basic";
      } else this.theme = theme;
    },
    setThemeType() {
      if (this.theme) {
        localStorage.setItem("date-picker-style", this.theme);
      }
    },
  },
  computed: {
    themeLabel() {
      return this.theme === "Classic"
        ? this.$t("dashboard_date_picker_theme_classic")
        : this.$t("dashboard_date_picker_theme_basic");
    },
    ranges: function () {
      if (this.theme === "Basic") return false;

      let today = new Date();
      today.setHours(0, 0, 0, 0);

      let yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);
      yesterday.setHours(0, 0, 0, 0);

      return {
        [this.$t("dashboard_date_ranges_today")]: [today, today],
        [this.$t("dashboard_date_ranges_yesterday")]: [yesterday, yesterday],
        [this.$t("dashboard_date_ranges_last_7_days")]: [
          new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7),
          new Date(today.getFullYear(), today.getMonth(), today.getDate()),
        ],
        [this.$t("dashboard_date_ranges_last_30_days")]: [
          new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30),
          new Date(today.getFullYear(), today.getMonth(), today.getDate()),
        ],
        [this.$t("dashboard_date_ranges_this_month")]: [
          new Date(today.getFullYear(), today.getMonth(), 1),
          new Date(today.getFullYear(), today.getMonth(), today.getDate()),
        ],
        [this.$t("dashboard_date_ranges_last_month")]: [
          new Date(today.getFullYear(), today.getMonth() - 1, 1),
          new Date(today.getFullYear(), today.getMonth(), 0),
        ],
        [this.$t("dashboard_date_ranges_this_year")]: [
          new Date(today.getFullYear(), 0, 1),
          new Date(today.getFullYear(), today.getMonth(), today.getDate()),
        ],
        [this.$t("dashboard_date_ranges_last_year")]: [
          new Date(today.getFullYear() - 1, 0, 1),
          new Date(today.getFullYear() - 1, 11, 31),
        ],
      };
    },
  },
  mounted() {
    this.updateDateValue();
    this.getThemeType();
  },
  watch: {
    theme(val) {
      const color = val === "Classic" ? "#fbfaf8" : "white";
      document.documentElement.style.setProperty(
        "--dynamic-datepicker-bg",
        color
      );
    },
  },
};
</script>

<style lang="scss">
.table-condensed {
  // .off {
  //   display: none;
  // }
  .off.in-range {
    background-color: #ebf4f8 !important;
  }
  .end-date {
    background-color: rgba(183, 4, 126, 0.8) !important;
  }
}
.weekend.active.in-range.end-date {
  background-color: rgba(183, 4, 126, 0.8) !important;
}
.date-range-selector {
  margin-top: 10px !important;
  .v-input__control {
    .v-input__slot {
      font-size: 12px;
      font-weight: 600;
    }
  }
}
.vue-daterange-picker {
  .form-control {
    border: none;
  }
}
.show-calendar {
  background-color: var(--dynamic-datepicker-bg, "white");
}

/* Classic/Basic theme toggle: isolate from document RTL so the thumb stays on the track */
.date-picker-theme-switch-wrap {
  direction: ltr;
}

.date-picker-theme-switch-wrap ::v-deep .v-input--selection-controls__input {
  flex-shrink: 0;
}

/*
  Vuetify 2 ships RTL-only transforms on `.v-input--switch--inset` (via
  `.v-application--is-rtl`) that translate the thumb by -6px (off) and -26px
  (on), which detaches it from the track. The `direction: ltr` wrapper above
  doesn't undo those because the rules key off the root RTL class. Re-assert
  the LTR transforms here so the thumb stays on the track in RTL.
*/
.v-application--is-rtl
  .date-picker-theme-switch-wrap
  .v-input--switch.v-input--switch--inset {
  .v-input--selection-controls__ripple,
  .v-input--switch__thumb {
    transform: translate(0, 0) !important;
  }

  &.v-input--is-dirty {
    .v-input--selection-controls__ripple,
    .v-input--switch__thumb {
      transform: translate(20px, 0) !important;
    }
  }
}
</style>
